import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../store/hooks.js';
import { dismissToast } from './toast-store.js';
import styles from './Toasts.module.css';

const DISMISS_AFTER_MS = 4500;

/**
 * Renders the toast stack (S03: "screen-reader announced").
 *
 * The container is a polite live region, so an assistive technology reads a new
 * toast without stealing focus. Errors are `assertive` — a failure the user needs
 * to know about now is worth interrupting for.
 */
export function Toasts(): JSX.Element {
  const items = useAppSelector((s) => s.toasts.items);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (items.length === 0) return;
    const timers = items.map((t) =>
      window.setTimeout(() => dispatch(dismissToast(t.id)), DISMISS_AFTER_MS),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [items, dispatch]);

  return (
    <div className={styles.stack}>
      {items.map((toast) => (
        <div
          key={toast.id}
          className={`${styles.toast} ${styles[toast.kind]}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
        >
          <span>{toast.message}</span>
          <button
            type="button"
            className={styles.close}
            onClick={() => dispatch(dismissToast(toast.id))}
            aria-label="Dismiss"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );
}
